import React, { useCallback, useState, useEffect, useContext, createContext } from 'react'

import { getOfferCatalogs } from '@portal/kit/pkg/services'
import { ConfigurationContext } from '@portal/kit/pkg/components'

import paymentServices, { isStripePayment } from 'helpers/payment-services'

export const PaymentContext = createContext()

const PaymentProvider = ({ children }) => {
  const portalConfiguration = useContext(ConfigurationContext)
  const isStripe = isStripePayment(portalConfiguration)
  const [catalogs, setCatalogs] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [order, setOrder] = useState(null);
  const [boughtProducts, setBoughtProducts] = useState(null);
  const [paymentError, setPaymentError] = useState(null);

  const loadOfferCatalogs = async () => {
    try {
      const response = await getOfferCatalogs()
      setCatalogs(response.data)
      return response.data
    }
    catch (e) {
      const error = e?.response?.data?.error?.message || 'form.error'
      setPaymentError(error)
      return []
    }
  }

  useEffect(() => {
    loadOfferCatalogs()
  }, [])

  const getCatalogs = useCallback(async () => (catalogs || loadOfferCatalogs()), [catalogs])

  const getOffer = useCallback(async (offerId) => {
    if (selectedOffer && selectedOffer.id === parseInt(offerId)) return selectedOffer

    const catalogs = await getCatalogs()
    const offer = catalogs.map(c => c.offers).flat().find(o => o.id === parseInt(offerId))
    setSelectedCatalog(catalogs.find(c => (c.offers || []).find(o => o.id === parseInt(offerId))))
    setSelectedOffer(offer)

    return offer
  }, [getCatalogs, selectedOffer])

  // Une commande "pending" n'est pas mise en cache : son statut évolue
  // (cf. ConfirmationInProgress qui la recharge jusqu'à confirmation)
  const loadOrder = useCallback(async (orderId) => {
    if (order && order.id === parseInt(orderId) && order.status !== 'pending') return order

    try {
      const { getOrder } = paymentServices(isStripe)
      const response = await getOrder(orderId, null)
      setOrder(response.data.order)
      setBoughtProducts(response.data)
      return response.data.order
    } catch (e) {
      if (e?.response?.status === 400 && e.response.data.order) {
        const o = e.response.data.order
        setOrder(o)
        if (o.status === 'failed') {
          setPaymentError(`portal.offers.payment_failed.errors.${o.payment_error.code}`)
        }
        return o
      } else {
        setPaymentError('portal.offers.payment_failed.errors.technical_error')
      }
    }
  }, [order, isStripe])

  const paymentData = {
    isStripe,
    catalogs,
    selectedCatalog,
    selectedOffer,
    paymentError,
    order,
    boughtProducts,
    getOffer,
    loadOrder
  }

  return (
    <PaymentContext.Provider value={paymentData}>
      {children}
    </PaymentContext.Provider>
  )
}

export default PaymentProvider
